import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Heading } from "../../ui/heading";
import { Button } from "../../ui/button";
import { Separator } from "../../ui/separator";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../ui/form";
import Sidebar from "../../DashboardNavigation/sidebar";
import Header from "../../DashboardNavigation/header";
import BreadCrumb from "../../breadcrumb";
import {
  SupportFormValues,
  supportSchema,
  UpdatedSupportFormValues,
  updatedSupportSchema,
} from "./communication-zod-schema";
import { Input } from "../../ui/input";
import PdfFileUpload from "../../FileUpload/PdfFileUpload";
import {
  getSupportsById,
  handleCreateSupports,
  handleUpdateSupports,
} from "../../../API/Mediatheque/Support/supportService";

export const CommunicationSupportForm: React.ComponentType<any> = () => {
  const uploadUrl = process.env.REACT_APP_FILE_UPLOAD;
  const { id } = useParams();
  const initialData = id ? parseInt(id) : undefined;
  const router = useNavigate();
  const [loading, setLoading] = useState(false);
  const title = initialData ? "Modifier le fichier" : "Ajouter un fichier";
  const description = initialData
    ? "Modifier le fichier"
    : "Ajouter un nouveau fichier";
  const toastMessage = initialData ? "Fichier modifié." : "Fichier ajoutée";
  const action = initialData ? "Mise à jour" : "Ajouter";

  const defaultValues = initialData
    ? initialData
    : {
        title: "",
        imgUrl: [],
      };

  const form = useForm<SupportFormValues | UpdatedSupportFormValues>({
    resolver: zodResolver(initialData ? updatedSupportSchema : supportSchema),
    mode: "onChange",
  });

  const onSubmit = async () => {
    setLoading(true);
    const formData = form.getValues();
    try {
      if (initialData) {
        await handleUpdateSupports(initialData, formData, router);
      } else {
        await handleCreateSupports(formData, router);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const breadcrumbItems = [
    {
      title: "Support de communications",
      link: "/admin-dashboard/mediatheque/support-de-communications",
    },
    {
      title: initialData ? "Mise à jour" : "Ajouter",
      link: "/admin-dashboard/mediatheque/support-de-communications/ajouter",
    },
  ];

  const [data, setData] = useState<any>();
  useEffect(() => {
    const fetchData = async () => {
      if (initialData) {
        const supportData = await getSupportsById(initialData);

        setData(supportData);
        form.reset({
          title: supportData.title,

          file: supportData.file
            ? {
                fileName: supportData.file,
                name: supportData.file,
                fileSize: 0,
                size: 0,
                key: supportData.file,
              }
            : undefined,
        });
      }
    };

    if (initialData) {
      fetchData();
    }
  }, [initialData]);

  return (
    <>
      <Header />
      <div className="flex h-screen overflow-hidden">
        <Sidebar />
        <div className="w-full p-10">
          <BreadCrumb items={breadcrumbItems} />
          <div className="flex items-center justify-between">
            <Heading title={title} description={description} />
          </div>
          <Separator />
          <Form {...form}>
            <form
              className="w-full space-y-8"
              onSubmit={form.handleSubmit(onSubmit)}
            >
              <div className="flex justify-between">
                <div className="w-1/2">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Titre </FormLabel>
                        <FormControl>
                          <Input
                            disabled={loading}
                            placeholder={"Titre..."}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {data?.file && (
                <div className="mt-4 mb-4">
                  <a
                    href={`${uploadUrl}/medias/support/${data.file}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 underline"
                  >
                    Consulter l'ancien PDF
                  </a>
                </div>
              )}
              <FormField
                control={form.control}
                name="file"
                render={() => (
                  <FormItem>
                    <FormControl>
                      <PdfFileUpload
                        onChange={(files) => {
                          const file = files[0];
                          if (file) {
                            const mappedFile = {
                              key: file.id,
                              fileName: file.fileName,
                              name: file.fileName,
                              fileSize: parseInt(file.fileSize),
                              size: parseInt(file.fileSize),
                              file: file.file,
                            };
                            form.setValue("file", mappedFile);
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex space-x-5">
                <div>
                  <Button
                    disabled={loading}
                    isLoading={loading}
                    className="ml-auto"
                    type="submit"
                  >
                    {action}
                  </Button>
                </div>
                <div>
                  <Button
                    onClick={() => router(-1)}
                    disabled={loading}
                    isLoading={loading}
                    className="ml-auto bg-red-500"
                    type="button"
                  >
                    {"Annuler"}
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </>
  );
};
